import { ABILITIES, abilityById, autoFor } from "../content/abilities";
import {
  addShield,
  applyDamage,
  applyHeal,
  applyStatus,
  destroyCorpse,
  downUnit,
  liveCover,
  nextId,
  stealHealth,
} from "./combat";
import { alliesOf, attackCdMult, canAct, canCast, canSee, classOf, evalPred, faceToward, foesOf, hasStatus, inArc, isActable, isMoving, nearby, pickClosest } from "./query";
import { interruptCast, stripOneCondition, stripPolarity } from "./status";
import {
  ARENA_H,
  ARENA_W,
  type AbilityDef,
  type Delay,
  type EffectOp,
  type Projectile,
  type Unit,
  type World,
} from "./types";
import { ang, circleHitsAabb, clamp, closestOnAabb, dist, fromAngle, norm, sub, vec } from "./vec";
import { spawnMinion, spawnSpirit } from "./summon";

const BASIC_CONE = Math.cos((110 * Math.PI) / 180 / 2);

export function coverBlocksPoint(world: World, x: number, y: number, r: number): boolean {
  for (const c of liveCover(world)) {
    if (circleHitsAabb(x, y, r, c.x, c.y, c.w, c.h)) return true;
  }
  return false;
}

export function resolvePos(world: World, x: number, y: number, r: number, ignoreId = -1): { x: number; y: number } {
  let px = clamp(x, r + 0.15, ARENA_W - r - 0.15);
  let py = clamp(y, r + 0.15, ARENA_H - r - 0.15);
  for (let iter = 0; iter < 4; iter++) {
    for (const c of liveCover(world)) {
      if (!circleHitsAabb(px, py, r, c.x, c.y, c.w, c.h)) continue;
      const hit = closestOnAabb(px, py, c.x, c.y, c.w, c.h);
      const dx = px - hit.x;
      const dy = py - hit.y;
      const d = Math.hypot(dx, dy);
      if (d < 1e-6) {
        px = clamp(px + 0.08, r, ARENA_W - r);
        py = clamp(py + 0.08, r, ARENA_H - r);
      } else {
        const push = (r - d + 0.01) / d;
        px += dx * push;
        py += dy * push;
      }
    }
    for (const u of world.units) {
      if (!isActable(u) && u.kind !== "corpse") continue;
      if (u.dead && u.kind !== "corpse") continue;
      if (u.id === ignoreId) continue;
      const dx = px - u.pos.x;
      const dy = py - u.pos.y;
      const d = Math.hypot(dx, dy);
      const min = r + u.radius * 0.72;
      if (d > 1e-6 && d < min) {
        const push = (min - d) / d;
        px += dx * push * 0.55;
        py += dy * push * 0.55;
      }
    }
    px = clamp(px, r + 0.15, ARENA_W - r - 0.15);
    py = clamp(py, r + 0.15, ARENA_H - r - 0.15);
  }
  return { x: px, y: py };
}

export function visibleFoes(world: World, unit: Unit): Unit[] {
  return foesOf(world, unit).filter((f) => canSee(world, unit, f) && f.kind !== "corpse");
}

export function nearestFoeInCone(world: World, unit: Unit, range: number, aimX: number, aimY: number): Unit | null {
  const facing = fromAngle(ang(sub(vec(aimX, aimY), unit.pos)));
  let best: Unit | null = null;
  let bestD = range + 0.001;
  for (const f of visibleFoes(world, unit)) {
    const d = dist(unit.pos, f.pos);
    if (d > range + f.radius * 0.25) continue;
    const dir = norm(sub(f.pos, unit.pos));
    if (dir.x === 0 && dir.y === 0) {
      if (d < bestD) {
        best = f;
        bestD = d;
      }
      continue;
    }
    const aligned = dir.x * facing.x + dir.y * facing.y;
    if (aligned < BASIC_CONE && d > 1.1) continue;
    if (d < bestD) {
      best = f;
      bestD = d;
    }
  }
  return best;
}

function candidates(world: World, unit: Unit, def: AbilityDef): Unit[] {
  if (def.target === "foe") return visibleFoes(world, unit);
  if (def.target === "ally") return alliesOf(world, unit, true).filter((u) => u.kind !== "corpse" || u.id === unit.id);
  if (def.target === "allyOrSpirit") {
    return world.units.filter(
      (u) => !u.dead && (u.kind === "spirit" || (u.team === unit.team && u.kind !== "corpse")),
    );
  }
  if (def.target === "any") {
    return world.units.filter((u) => isActable(u) || u.kind === "corpse" || u.kind === "spirit");
  }
  if (def.target === "self") return [unit];
  return [];
}

function inAbilityRange(unit: Unit, target: Unit, def: AbilityDef): boolean {
  const range = def.range || 12;
  return dist(unit.pos, target.pos) <= range + target.radius * 0.3;
}

export function pickTarget(world: World, unit: Unit, def: AbilityDef, aimX: number, aimY: number): Unit | null {
  if (def.target === "self" || def.target === "none") return unit;
  const pool = candidates(world, unit, def);
  if (unit.autoTargetId != null) {
    const locked = pool.find((u) => u.id === unit.autoTargetId);
    if (locked && inAbilityRange(unit, locked, def)) return locked;
  }
  return pickClosest(pool, aimX, aimY, def.range || 12, unit);
}

export function currentAuto(unit: Unit): AbilityDef | null {
  const id = autoFor(unit.classId, unit.state.weapon ?? 0);
  return id ? ABILITIES[id] : null;
}

export function resolveAutoTarget(world: World, unit: Unit): Unit | null {
  const def = currentAuto(unit);
  if (!def) return null;
  const pool = candidates(world, unit, def).filter((u) => u.id !== unit.id && inAbilityRange(unit, u, def));
  if (unit.autoTargetId != null) {
    const locked = world.units.find((u) => u.id === unit.autoTargetId) ?? null;
    if (locked && pool.some((u) => u.id === locked.id)) return locked;
    if (!locked || locked.dead && locked.kind !== "corpse") unit.autoTargetId = null;
    else return null;
  }
  let best: Unit | null = null;
  let bestD = Infinity;
  for (const u of pool) {
    const d = dist(unit.pos, u.pos);
    if (d < bestD) {
      best = u;
      bestD = d;
    }
  }
  return best;
}

function applyOps(world: World, target: Unit, source: Unit, ops: EffectOp[], predTarget = target): void {
  for (const op of ops) {
    if (op.op === "statusIf" && op.pred && !evalPred(op.pred, world, predTarget, source)) continue;
    if (op.op === "status" || op.op === "statusIf") {
      if (op.id && op.duration) applyStatus(world, target, op.id, op.duration, source);
    } else if (op.op === "stripPolarity" && op.polarity) {
      stripPolarity(target, op.polarity);
    } else if (op.op === "removeStatus" && op.id) {
      target.statuses = target.statuses.filter((s) => s.id !== op.id);
    } else if (op.op === "shield" && op.amount) {
      addShield(target, op.amount, {
        kind: op.shieldKind ?? "any",
        decay: op.decay ?? 0,
        max: op.amount,
        tag: op.tag ?? "",
        sourceId: source.id,
      });
    } else if (op.op === "interrupt") {
      interruptCast(world, target);
    } else if (op.op === "stealHealth" && op.amount) {
      stealHealth(world, target, source, op.amount, source);
    }
  }
}

function hitUnit(
  world: World,
  target: Unit,
  source: Unit,
  def: AbilityDef,
  amount: number,
  extra?: { guaranteedCrit?: boolean },
): void {
  const critIf = def.critIf ? evalPred(def.critIf, world, target, source) : false;
  let dmg = amount;
  if (def.bonusIf && evalPred(def.bonusIf.pred, world, target, source)) dmg += def.bonusIf.amount;
  const alreadyCrippled = hasStatus(target, "cripple");
  const res = applyDamage(world, target, dmg, source, {
    type: def.damageType,
    melee: def.kind === "meleeArc" || def.kind === "chargeLine",
    skillId: def.id,
    guaranteedCrit: extra?.guaranteedCrit || critIf,
  });
  const hitOps = alreadyCrippled
    ? def.onHit
    : def.onHit.filter((op) => !(op.op === "statusIf" && op.pred === "hasCripple" && op.id === "stagger") || alreadyCrippled);
  applyOps(world, target, source, hitOps, target);
  if (res.crit) applyOps(world, target, source, def.onCrit, target);
  if (source.classId === "ranger" && (source.state.poisonCharges ?? 0) > 0 && def.slot === "auto") {
    applyStatus(world, target, "poison", 5, source);
    source.state.poisonCharges -= 1;
  }
}

function spawnPulse(world: World, x: number, y: number, r: number, kind: "aoe" | "heal" | "slam" | "dash", team: Unit["team"]): void {
  world.pulses.push({
    id: nextId(world),
    pos: { x, y },
    radius: r,
    life: 0.38,
    maxLife: 0.38,
    kind,
    team,
  });
}

function spawnProj(
  world: World,
  owner: Unit,
  aimX: number,
  aimY: number,
  def: AbilityDef,
  guaranteedCrit: boolean,
): Projectile {
  const dir = norm(sub(vec(aimX, aimY), owner.pos));
  const face = dir.x === 0 && dir.y === 0 ? fromAngle(owner.facing) : dir;
  const proj: Projectile = {
    id: nextId(world),
    ownerId: owner.id,
    team: owner.team,
    pos: { x: owner.pos.x + face.x * (owner.radius + 0.12), y: owner.pos.y + face.y * (owner.radius + 0.12) },
    vel: { x: face.x * def.projectileSpeed, y: face.y * def.projectileSpeed },
    radius: def.projectileRadius || 0.13,
    damage: def.damage,
    remaining: def.range / Math.max(def.projectileSpeed, 1) + 0.04,
    abilityId: def.id,
    damageType: def.damageType,
    onHit: def.onHit,
    onCrit: def.onCrit,
    critIf: def.critIf,
    bonusIf: def.bonusIf,
    guaranteedCrit,
  };
  world.projectiles.push(proj);
  return proj;
}

function hitCoverAt(world: World, x: number, y: number, r: number): boolean {
  for (const c of liveCover(world)) {
    if (circleHitsAabb(x, y, r, c.x, c.y, c.w, c.h)) return true;
  }
  return false;
}

export function tickProjectiles(world: World, dt: number): void {
  const remain: Projectile[] = [];
  for (const p of world.projectiles) {
    p.remaining -= dt;
    if (p.remaining <= 0) continue;
    p.pos.x += p.vel.x * dt;
    p.pos.y += p.vel.y * dt;
    if (p.pos.x < -0.5 || p.pos.y < -0.5 || p.pos.x > ARENA_W + 0.5 || p.pos.y > ARENA_H + 0.5) continue;
    if (hitCoverAt(world, p.pos.x, p.pos.y, p.radius)) continue;
    const src = world.units.find((s) => s.id === p.ownerId) ?? null;
    if (!src) continue;
    const def = ABILITIES[p.abilityId];
    let consumed = false;
    for (const u of world.units) {
      if (u.team === p.team) continue;
      if (u.dead && u.kind !== "corpse") continue;
      if (u.kind === "corpse" && u.classId !== "cultist") continue;
      if (dist(u.pos, p.pos) > u.radius + p.radius) continue;
      if (!canSee(world, src, u) && u.classId === "assassin") continue;
      if (def) hitUnit(world, u, src, def, p.damage, { guaranteedCrit: p.guaranteedCrit });
      consumed = true;
      break;
    }
    if (!consumed) remain.push(p);
  }
  world.projectiles = remain;
}

function noteAttack(unit: Unit, world: World): void {
  unit.lastAttackAt = world.time;
  if (hasStatus(unit, "invisible") && !(unit.classId === "assassin" && world.zones.some((z) => z.kind === "smoke" && z.ownerId === unit.id))) {
    unit.statuses = unit.statuses.filter((s) => s.id !== "invisible");
  }
}

export function executeAbility(world: World, caster: Unit, def: AbilityDef, aimX: number, aimY: number): boolean {
  faceToward(caster, aimX, aimY);
  applyOps(world, caster, caster, def.selfOnCast, caster);
  noteAttack(caster, world);

  if (def.kind === "custom") return resolveCustom(world, caster, def, aimX, aimY);

  if (def.kind === "meleeArc") {
    const foes = visibleFoes(world, caster).filter((f) => inArc(caster, f, def.range, def.arc || 1.6));
    if (!foes.length) return false;
    foes.sort((a, b) => dist(caster.pos, a.pos) - dist(caster.pos, b.pos));
    const closest = foes[0];
    for (const f of foes) {
      const amt = f.id === closest.id ? def.damage : def.splash || def.damage;
      hitUnit(world, f, caster, def, amt);
    }
    spawnPulse(world, caster.pos.x, caster.pos.y, def.range, "aoe", caster.team);
    return true;
  }

  if (def.kind === "projectile") {
    const target = pickTarget(world, caster, def, aimX, aimY);
    const tx = target ? target.pos.x : aimX;
    const ty = target ? target.pos.y : aimY;
    const base = ang(sub(vec(tx, ty), caster.pos));
    const n = Math.max(1, def.projectileCount);
    const spread = def.projectileSpread;
    for (let i = 0; i < n; i++) {
      const t = n === 1 ? 0 : (i / (n - 1) - 0.5) * 2;
      const a = fromAngle(base + t * spread);
      spawnProj(world, caster, caster.pos.x + a.x * 4, caster.pos.y + a.y * 4, def, false);
    }
    return true;
  }

  if (def.kind === "dash") {
    const dir = norm(sub(vec(aimX, aimY), caster.pos));
    const face = dir.x === 0 && dir.y === 0 ? fromAngle(caster.facing) : dir;
    const distDash = def.dashDistance || def.range;
    const dest = resolvePos(
      world,
      caster.pos.x + face.x * distDash,
      caster.pos.y + face.y * distDash,
      caster.radius,
      caster.id,
    );
    caster.pos.x = dest.x;
    caster.pos.y = dest.y;
    spawnPulse(world, dest.x, dest.y, 0.8, "dash", caster.team);
    return true;
  }

  if (def.kind === "chargeLine") {
    if (def.id === "hallowed_ground") {
      caster.charging = {
        abilityId: def.id,
        kind: "slam",
        remaining: def.windup,
        dirX: aimX,
        dirY: aimY,
        traveled: 0,
        maxDist: 0,
        speed: 0,
      };
      return true;
    }
    const dir = norm(sub(vec(aimX, aimY), caster.pos));
    const face = dir.x === 0 && dir.y === 0 ? fromAngle(caster.facing) : dir;
    caster.charging = {
      abilityId: def.id,
      kind: "line",
      remaining: def.windup,
      dirX: face.x,
      dirY: face.y,
      traveled: 0,
      maxDist: def.range,
      speed: def.chargeSpeed,
    };
    return true;
  }

  if (def.kind === "target") {
    const t = pickTarget(world, caster, def, aimX, aimY);
    if (!t) return false;
    if (def.damage > 0) hitUnit(world, t, caster, def, def.damage);
    else applyOps(world, t, caster, def.onHit, t);
    if (def.heal > 0) applyHeal(world, t, def.heal, caster, { skillId: def.id });
    grantSpiritArms(world, caster, t);
    return true;
  }

  if (def.kind === "self") {
    if (def.heal > 0) applyHeal(world, caster, def.heal, caster, { skillId: def.id });
    return true;
  }

  if (def.kind === "areaDelay") {
    const aim = clampAim(caster, aimX, aimY, def.range);
    world.delays.push({
      id: nextId(world),
      kind: def.id,
      remaining: def.delay,
      pos: { x: aim.x, y: aim.y },
      radius: def.radius,
      ownerId: caster.id,
    } satisfies Delay);
    spawnPulse(world, aim.x, aim.y, def.radius, "heal", caster.team);
    return true;
  }

  if (def.kind === "zone") {
    world.zones.push({
      id: nextId(world),
      kind: def.zoneKind || def.id,
      pos: { x: caster.pos.x, y: caster.pos.y },
      radius: def.radius,
      remaining: def.zoneDuration,
      team: caster.team,
      ownerId: caster.id,
    });
    spawnPulse(world, caster.pos.x, caster.pos.y, def.radius, "aoe", caster.team);
    return true;
  }

  if (def.kind === "swap") {
    caster.state.weapon = (caster.state.weapon ?? 0) === 0 ? 1 : 0;
    caster.basicCd = 0.15;
    return true;
  }

  return false;
}

function clampAim(unit: Unit, aimX: number, aimY: number, range: number): { x: number; y: number } {
  if (range <= 0) return { x: unit.pos.x, y: unit.pos.y };
  const dx = aimX - unit.pos.x;
  const dy = aimY - unit.pos.y;
  const d = Math.hypot(dx, dy);
  if (d <= range || d < 1e-6) return { x: aimX, y: aimY };
  return { x: unit.pos.x + (dx / d) * range, y: unit.pos.y + (dy / d) * range };
}

function grantSpiritArms(world: World, caster: Unit, target: Unit): void {
  if (caster.classId !== "ritualist") return;
  if (target.team !== caster.team && target.kind !== "spirit") return;
  applyStatus(world, target, "spirit_arms", 6, caster);
}

function priestHeal(world: World, caster: Unit, target: Unit, amount: number, skillId: string): void {
  applyHeal(world, target, amount, caster, { skillId });
}

function resolveCustom(world: World, caster: Unit, def: AbilityDef, aimX: number, aimY: number): boolean {
  if (def.id === "mending_touch") {
    const t = pickTarget(world, caster, def, aimX, aimY);
    if (!t) return false;
    const guilty = hasStatus(t, "ire");
    priestHeal(world, caster, t, def.heal, def.id);
    const stripped = stripOneCondition(t);
    if (stripped) {
      if (guilty && caster.classId === "priest") applyStatus(world, t, "burning", 4, caster);
      else applyStatus(world, t, "regen", 4, caster);
    }
    return true;
  }

  if (def.id === "be_not_afraid") {
    const t = pickTarget(world, caster, def, aimX, aimY);
    if (!t) return false;
    applyStatus(world, t, "regen", 5, caster);
    addShield(t, 4, { kind: "any", max: 4, decay: 0.8, tag: "faith", sourceId: caster.id });
    applyStatus(world, t, "faith", 5, caster);
    grantSpiritArms(world, caster, t);
    return true;
  }

  if (def.id === "reap") {
    const range = def.range;
    const corpseMode = caster.kind === "corpse" && caster.state.selfCorpse === 1;
    const pool = world.units.filter((u) => (isActable(u) || u.kind === "corpse") && u.id !== caster.id && dist(u.pos, caster.pos) <= range + u.radius);
    const one = corpseMode ? null : pickClosest(pool, aimX, aimY, range, caster);
    const targets = corpseMode ? pool : one ? [one] : [];
    if (!targets.length) return false;
    for (const t of targets) {
      const ripe = hasStatus(t, "bleeding") || hasStatus(t, "curse") || t.hp <= 2 || t.kind === "corpse";
      applyStatus(world, t, "bleeding", 4, caster);
      if (ripe) stealHealth(world, t, caster, 1, caster);
      if (t.kind === "corpse" && t.hp <= 0) {
        destroyCorpse(world, t);
        spawnMinion(world, caster, "skeleton", t.pos.x, t.pos.y);
      }
    }
    return true;
  }

  if (def.id === "life_transfer") {
    const t = pickTarget(world, caster, def, aimX, aimY);
    if (!t || t.id === caster.id) return false;
    const sac = Math.floor(caster.hp / 2);
    if (sac < 1) return false;
    caster.hp -= sac;
    applyHeal(world, t, sac * 2, caster, { skillId: def.id });
    applyStatus(world, t, "regen", 4, caster);
    if (caster.hp <= 0) downUnit(world, caster, caster);
    return true;
  }

  if (def.id === "flesh_of_my_flesh") {
    const hp = caster.hp;
    caster.hp = 0;
    downUnit(world, caster, caster);
    spawnMinion(world, caster, "golem", caster.pos.x, caster.pos.y);
    void hp;
    return true;
  }

  if (def.id === "phantom_pain") {
    const t = pickTarget(world, caster, def, aimX, aimY);
    if (!t) return false;
    if (!hasStatus(t, "regen")) {
      applyDamage(world, t, 4, caster, { type: "psychic", skillId: def.id });
      applyStatus(world, t, "regen", 3, caster);
    } else {
      applyDamage(world, t, 1, caster, { type: "psychic", skillId: def.id });
    }
    return true;
  }

  if (def.id === "blackout") {
    const t = pickTarget(world, caster, { ...def, target: "any" }, aimX, aimY) ?? caster;
    interruptCast(world, t);
    applyStatus(world, t, "cripple", 3.5, caster);
    applyStatus(world, t, "blind", 3.5, caster);
    applyStatus(world, t, "silence", 3.5, caster);
    applyStatus(world, t, "warded", 4, caster);
    return true;
  }

  if (def.id === "right_from_wrong") {
    for (const u of nearby(world, caster, def.radius || 3.6, true)) {
      if (u.team !== caster.team) stripPolarity(u, "enchantment");
      else stripPolarity(u, "condition");
    }
    spawnPulse(world, caster.pos.x, caster.pos.y, def.radius || 3.6, "aoe", caster.team);
    return true;
  }

  if (def.id === "lesser_invocation") {
    const t = pickTarget(world, caster, def, aimX, aimY);
    if (!t) return false;
    if (t.team === caster.team || t.kind === "spirit") {
      applyHeal(world, t, def.heal, caster, { skillId: def.id });
      applyStatus(world, t, "regen", 4, caster);
      grantSpiritArms(world, caster, t);
    } else {
      applyDamage(world, t, def.damage, caster, { skillId: def.id });
      applyStatus(world, t, "burning", 4, caster);
    }
    return true;
  }

  if (def.id === "invocation") {
    const t = pickTarget(world, caster, def, aimX, aimY);
    if (!t) return false;
    invocationPulse(world, caster, t, def.heal, def.damage, def.radius);
    grantSpiritArms(world, caster, t);
    return true;
  }

  if (def.id === "soul_cleave") {
    const t = pickTarget(world, caster, def, aimX, aimY);
    if (!t) return false;
    const lost = Math.max(1, Math.floor(t.hp / 2));
    t.hp -= lost;
    const spirit = spawnSpirit(world, caster, t.pos.x, t.pos.y, lost);
    invocationPulse(world, caster, spirit, 2, 2, def.radius);
    if (t.hp <= 0) downUnit(world, t, caster);
    return true;
  }

  if (def.id === "rally") {
    const allies = alliesOf(world, caster, true).filter((a) => dist(a.pos, caster.pos) <= (def.radius || 4.5) + a.radius);
    allies.sort((a, b) => a.hp / a.maxHp - b.hp / b.maxHp);
    for (const a of allies) {
      applyStatus(world, a, "rally", 4, caster);
      const weight = a.hp / a.maxHp < 0.5 ? 2 : 1;
      applyHeal(world, a, def.heal * weight, caster, { skillId: def.id });
    }
    spawnPulse(world, caster.pos.x, caster.pos.y, def.radius || 4.5, "heal", caster.team);
    return true;
  }

  if (def.id === "turn_the_tide") {
    for (const a of alliesOf(world, caster, true).filter((u) => dist(u.pos, caster.pos) <= (def.radius || 4.8))) {
      applyStatus(world, a, "tide", 5, caster);
      applyStatus(world, a, "fleet", 5, caster);
      applyStatus(world, a, "frenzy", 5, caster);
      applyHeal(world, a, def.heal, caster, { skillId: def.id });
    }
    spawnPulse(world, caster.pos.x, caster.pos.y, def.radius || 4.8, "heal", caster.team);
    return true;
  }

  if (def.id === "hammerfall") {
    const t = pickTarget(world, caster, def, aimX, aimY);
    if (!t) return false;
    const charge = caster.state.charge ?? 0;
    const bonus = Math.floor(charge * 3);
    const full = charge >= 0.99;
    caster.state.charge = 0;
    hitUnit(world, t, caster, { ...def, damage: def.damage + bonus }, def.damage + bonus, { guaranteedCrit: full });
    if (full) {
      spawnPulse(world, t.pos.x, t.pos.y, 2.1, "slam", caster.team);
      for (const f of visibleFoes(world, caster)) {
        if (dist(f.pos, t.pos) > 2.1) continue;
        if (f.id !== t.id) applyDamage(world, f, 2, caster, { melee: true, skillId: def.id });
        applyStatus(world, f, "stun", 1, caster);
      }
    }
    return true;
  }

  if (def.id === "sanctuary") {
    for (const a of alliesOf(world, caster, true).filter((u) => dist(u.pos, caster.pos) <= (def.radius || 3.4))) {
      const self = a.id === caster.id;
      applyStatus(world, a, "sanctuary", 5, caster);
      applyStatus(world, a, "regen", self ? 6 : 4, caster);
      addShield(a, self ? 4 : 2, { kind: "any", max: self ? 4 : 2, decay: 0.3, tag: "sanctuary", sourceId: caster.id });
      applyHeal(world, a, self ? def.heal + 1 : def.heal, caster, { skillId: def.id });
    }
    spawnPulse(world, caster.pos.x, caster.pos.y, def.radius || 3.4, "heal", caster.team);
    return true;
  }

  return false;
}

function invocationPulse(world: World, caster: Unit, focus: Unit, healAmt: number, dmgAmt: number, radius: number): void {
  const allyFocus = focus.team === caster.team || focus.kind === "spirit";
  if (allyFocus) {
    applyHeal(world, focus, healAmt, caster, { skillId: "invocation" });
    for (const f of foesOf(world, caster)) {
      if (dist(f.pos, focus.pos) <= radius + f.radius) applyDamage(world, f, dmgAmt, caster, { skillId: "invocation" });
    }
  } else {
    applyDamage(world, focus, dmgAmt, caster, { skillId: "invocation" });
    for (const a of alliesOf(world, caster, true)) {
      if (dist(a.pos, focus.pos) <= radius + a.radius) applyHeal(world, a, healAmt, caster, { skillId: "invocation" });
    }
  }
  spawnPulse(world, focus.pos.x, focus.pos.y, radius, allyFocus ? "heal" : "aoe", caster.team);
}

export function tickCharges(world: World, dt: number): void {
  for (const unit of world.units) {
    const ch = unit.charging;
    if (!ch) continue;
    if (ch.remaining > 0) {
      ch.remaining -= dt;
      if (ch.remaining > 0) continue;
      if (ch.kind === "slam") {
        finishHallowed(world, unit, ch.dirX, ch.dirY);
        unit.charging = null;
      }
      continue;
    }
    if (ch.kind === "line") {
      const step = ch.speed * dt;
      const next = resolvePos(world, unit.pos.x + ch.dirX * step, unit.pos.y + ch.dirY * step, unit.radius, unit.id);
      unit.pos.x = next.x;
      unit.pos.y = next.y;
      unit.facing = ang(vec(ch.dirX, ch.dirY));
      ch.traveled += step;
      let hit: Unit | null = null;
      for (const f of visibleFoes(world, unit)) {
        if (dist(f.pos, unit.pos) <= unit.radius + f.radius + 0.12) {
          hit = f;
          break;
        }
      }
      if (hit) {
        const def = abilityById(ch.abilityId);
        hitUnit(world, hit, unit, def, def.damage);
        if (isMoving(hit)) applyStatus(world, hit, "stun", 1.1, unit);
        unit.charging = null;
        spawnPulse(world, hit.pos.x, hit.pos.y, 0.9, "slam", unit.team);
      } else if (ch.traveled >= ch.maxDist) {
        unit.charging = null;
      }
    }
  }
}

function finishHallowed(world: World, caster: Unit, aimX: number, aimY: number): void {
  const def = abilityById("hallowed_ground");
  const aim = clampAim(caster, aimX, aimY, def.range);
  spawnPulse(world, aim.x, aim.y, def.radius, "slam", caster.team);
  for (const f of foesOf(world, caster)) {
    if (dist(f.pos, aim) <= def.radius + f.radius) {
      applyDamage(world, f, def.damage, caster, { melee: true, skillId: def.id });
      applyStatus(world, f, "stun", 1.2, caster);
    }
  }
  world.zones.push({
    id: nextId(world),
    kind: "hallowed",
    pos: { x: aim.x, y: aim.y },
    radius: def.radius,
    remaining: def.zoneDuration,
    team: caster.team,
    ownerId: caster.id,
  });
}

export function tickDelays(world: World, dt: number): void {
  const keep: Delay[] = [];
  for (const d of world.delays) {
    d.remaining -= dt;
    if (d.remaining > 0) {
      keep.push(d);
      continue;
    }
    const caster = world.units.find((u) => u.id === d.ownerId);
    if (!caster || d.kind !== "cleanse_evil") continue;
    const def = abilityById("cleanse_evil");
    spawnPulse(world, d.pos.x, d.pos.y, d.radius, "heal", caster.team);
    for (const u of world.units) {
      if (!isActable(u) && u.kind !== "spirit") continue;
      const dd = dist(u.pos, d.pos);
      if (dd > d.radius + u.radius) continue;
      const center = dd <= d.radius * 0.45;
      let heal = def.heal * (center ? 2 : 1);
      const crit = hasStatus(u, "burning");
      if (crit) heal *= 2;
      priestHeal(world, caster, u, heal, def.id);
      if (hasStatus(u, "ire") && caster.classId === "priest") applyStatus(world, u, "burning", 4, caster);
      else applyStatus(world, u, "regen", 4, caster);
    }
  }
  world.delays = keep;
}

export function tickZones(world: World, dt: number): void {
  for (const z of world.zones) z.remaining -= dt;
  world.zones = world.zones.filter((z) => z.remaining > 0);
  for (const z of world.zones) {
    if (z.kind !== "hallowed") continue;
    for (const u of world.units) {
      if (u.team === z.team || u.dead) continue;
      const d = dist(u.pos, z.pos);
      if (d <= z.radius - 0.05) continue;
      if (d > z.radius + 0.55) continue;
      const dir = norm(sub(z.pos, u.pos));
      const dest = resolvePos(world, z.pos.x + dir.x * (z.radius - 0.15), z.pos.y + dir.y * (z.radius - 0.15), u.radius, u.id);
      if (dist(u.prevPos, z.pos) <= z.radius + 0.1) {
        u.pos.x = dest.x;
        u.pos.y = dest.y;
      }
    }
  }
}

export function tickPulses(world: World, dt: number): void {
  for (const p of world.pulses) p.life -= dt;
  world.pulses = world.pulses.filter((p) => p.life > 0);
}

function slotCd(unit: Unit, slot: "auto" | "skill" | "ult"): number {
  if (slot === "auto") return unit.basicCd;
  if (slot === "skill") return unit.skillCd;
  return unit.ultCd;
}

function setSlotCd(unit: Unit, slot: "auto" | "skill" | "ult", v: number): void {
  if (slot === "auto") unit.basicCd = v;
  else if (slot === "skill") unit.skillCd = v;
  else unit.ultCd = v;
}

export function tryActivate(world: World, unit: Unit, slot: "auto" | "skill" | "ult", aimX: number, aimY: number): boolean {
  if (slot !== "auto" && !canCast(unit)) return false;
  if (slot === "auto" && !canAct(unit)) return false;
  if (unit.casting) return false;
  const def = slot === "auto" ? currentAuto(unit) : slot === "skill" ? (unit.skillCdMax ? ABILITIES[classOf(unit).skill] : undefined) : ABILITIES[classOf(unit).ult];
  if (!def) return false;
  if (slotCd(unit, slot) > 0) return false;
  if (slot === "auto") {
    const t = resolveAutoTarget(world, unit);
    if (!t && def.target !== "none" && def.target !== "self") return false;
    if (t) {
      aimX = t.pos.x;
      aimY = t.pos.y;
    }
  } else if (def.target !== "none" && def.target !== "self" && def.kind !== "chargeLine" && def.kind !== "dash" && def.kind !== "zone" && def.kind !== "swap") {
    if (!pickTarget(world, unit, def, aimX, aimY)) return false;
  }

  const cd = slot === "auto" ? unit.basicCdMax : def.cd;
  if (def.windup > 0 && def.kind !== "chargeLine") {
    setSlotCd(unit, slot, cd);
    unit.casting = {
      abilityId: def.id,
      remaining: def.windup,
      total: def.windup,
      aimX,
      aimY,
      targetId: pickTarget(world, unit, def, aimX, aimY)?.id ?? null,
    };
    return true;
  }

  const ok = executeAbility(world, unit, def, aimX, aimY);
  if (ok) setSlotCd(unit, slot, slot === "auto" ? unit.basicCdMax * attackCdMult(unit) : def.cd);
  return ok;
}

export function finishCasts(world: World): void {
  for (const unit of world.units) {
    if (!unit.casting || unit.casting.remaining > 0) continue;
    const def = ABILITIES[unit.casting.abilityId];
    const aimX = unit.casting.aimX;
    const aimY = unit.casting.aimY;
    unit.casting = null;
    if (def) executeAbility(world, unit, def, aimX, aimY);
  }
}

export function tryBasicAttack(world: World, unit: Unit, aimX: number, aimY: number): boolean {
  return tryActivate(world, unit, "auto", aimX, aimY);
}

export function trySkill(world: World, unit: Unit, aimX: number, aimY: number): boolean {
  return tryActivate(world, unit, "skill", aimX, aimY);
}

export function tryUlt(world: World, unit: Unit, aimX: number, aimY: number): boolean {
  return tryActivate(world, unit, "ult", aimX, aimY);
}
